<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<script setup lang="ts">
import { ref, computed, onMounted, watch, onUnmounted, type Ref } from "vue";
import { uniqBy } from "lodash-es";
import { usePermission } from "@vueuse/core";
import { useAnalyticManager } from "@magnit/analytic-events";
import {
  ShopSelectState,
  ShopSelectMapControls,
  ShopSelectShopType,
  ShopModalState,
} from "./enums";
import type {
  IVShopSelectProps,
  IMapCustomOptions,
  IMapBoundingBox,
  IShopSelectShop,
  IDisplayShop,
} from "./types";
import {
  fetchExternalScript,
  getDisplayConfiguration,
  boundsInBoundingBox,
  filterShopsInBoundingBox,
} from "./utils";
import {
  FILTERS_SHOPS_ARRAY,
  DEFAULT_MAP_GRID_SIZE,
  MAP_DEFAULT_MAP_OBJECTS_OPTIONS,
  MAP_DEFAULT_MAP_GEOLOCATION_OBJECT_OPTIONS,
  MAP_DEFAULT_MAP_CLUSTER_OPTIONS,
  MAP_DEFAULT_BALLOON_OPTIONS,
  MAP_DEFAULT_MARKER_OPTIONS,
  DEFAULT_YANDEX_MAP_KEY,
  DEFAULT_YANDEX_MAP_LANG,
  DEFAULT_YANDEX_MAP_VERSION,
  DEFAULT_MAP_CENTER,
  DEFAULT_MAP_ZOOM,
  DEFAULT_MIN_ZOOM,
  DEFAULT_MAX_ZOOM,
  DEFAULT_MIN_SHOPS_SEARCH_STRING_LENGTH,
  DEFAULT_SEARCH_UPDATE_DEBOUNCE_TIME,
} from "./constants";
import useDebounce from "./composables/useDebounce";
import ShopSelectCloseButton from "./components/ShopSelectCloseButton.vue";
import ShopSelectMap from "./components/ShopSelectMap.vue";
import ShopSelectModalButton from "./components/ShopSelectModalButton.vue";
import ShopSelectShops from "./components/ShopSelectShops.vue";

const props = withDefaults(defineProps<IVShopSelectProps>(), {
  yandexMapKey: DEFAULT_YANDEX_MAP_KEY,
  yandexMapLang: DEFAULT_YANDEX_MAP_LANG,
  yandexMapVersion: DEFAULT_YANDEX_MAP_VERSION,
  center: () => [...DEFAULT_MAP_CENTER],
  zoom: DEFAULT_MAP_ZOOM,
  minZoom: DEFAULT_MIN_ZOOM,
  maxZoom: DEFAULT_MAX_ZOOM,
  controls: () => [
    ShopSelectMapControls.ZOOM,
    ShopSelectMapControls.GEOLOCATION,
  ],
  gridSize: DEFAULT_MAP_GRID_SIZE,
  shopListInBoundingBox: true,
  boundingBox: null,
  mapObjectOptions: () => ({ ...MAP_DEFAULT_MAP_OBJECTS_OPTIONS }),
  geolocationObjectOptions: () => ({
    ...MAP_DEFAULT_MAP_GEOLOCATION_OBJECT_OPTIONS,
  }),
  clusterOptions: () => ({ ...MAP_DEFAULT_MAP_CLUSTER_OPTIONS }),
  balloonOptions: () => ({ ...MAP_DEFAULT_BALLOON_OPTIONS }),
  markerOptions: () => ({ ...MAP_DEFAULT_MARKER_OPTIONS }),
  modalOpen: false,
  shops: () => [],
  shopsSearchString: "",
  minShopsSearchStringLength: DEFAULT_MIN_SHOPS_SEARCH_STRING_LENGTH,
  selectedShop: undefined,
  targetShop: undefined,
  shopsFilter: () => [],
  shopsFilterOptions: null,
  clusterize: true,
  autocenterKey: 0,
  displayModal: true,
  displayCloseButton: true,
  shopsListLoading: false,
  mapLoading: false,
  searchUpdateDebounceTime: DEFAULT_SEARCH_UPDATE_DEBOUNCE_TIME,
});
const emit = defineEmits<{
  "ready:script": [];
  "error:script": [payload: { err: unknown }];
  "submit:boundingBox": [value: IMapBoundingBox];
  "submit:shopSelect": [payload: { shop: IShopSelectShop }];
  "submit:shopsSearch": [value: string];
  "close:shopSelect": [];
}>();

const innerState: Ref<ShopSelectState> = ref(ShopSelectState.LOADING);
const mapElementId: Ref<string> = ref("");

const geolocationPermission = usePermission("geolocation", { controls: true });
const { send } = useAnalyticManager();
const modalExpanded: Ref<ShopModalState> = ref(ShopModalState.NORMAL);
const targetShopRef = ref<IShopSelectShop | null>(props.targetShop ?? null);
const mapOptionsKeyRef = ref(1);

const modalOpen = defineModel<boolean>("modalOpen");
const shopsFilter = defineModel<ShopSelectShopType[]>("shopsFilter", {
  default: [],
});

const shopsSearchValueRef = ref(props.shopsSearchString);
const {
  call: handleSearchInput,
  clear: clearSearchDebounce,
  force: forceSearchSubmit,
} = useDebounce(() => {
  const searchStr = shopsSearchValueRef.value;
  if (
    searchStr === props.shopsSearchString ||
    (searchStr.length && searchStr.length < props.minShopsSearchStringLength)
  )
    return;
  send("ShopSelect:ListStore:Search:Click", { query: searchStr });
  emit("submit:shopsSearch", searchStr);
}, props.searchUpdateDebounceTime);
const shopsSearchValue = computed({
  get: () => shopsSearchValueRef.value,
  set: (v: string) => {
    if (shopsSearchValueRef.value === v) return;
    shopsSearchValueRef.value = v;
    handleSearchInput();
  },
});

const mapBoundingBoxRef = ref<IMapBoundingBox | null>(
  props.boundingBox ? { ...props.boundingBox } : null,
);
const { call: callBoundsChange, clear: clearBoundsChangeDebounce } =
  useDebounce((boundingBox: IMapBoundingBox) => {
    mapBoundingBoxRef.value = boundingBox;
    if (
      boundingBox === null ||
      (props.boundingBox && boundsInBoundingBox(props.boundingBox, boundingBox))
    ) {
      return;
    }
    emit("submit:boundingBox", boundingBox);
  }, 150);
const mapBoundingBox = computed({
  get: () => mapBoundingBoxRef.value,
  set: (v: IMapBoundingBox | null) => {
    callBoundsChange(v);
  },
});

const displayMap = computed(() => innerState.value === ShopSelectState.READY);
const shouldMapBeReinitialized = computed(() => ({
  controls: props.controls,
  gridSize: props.gridSize,
  mapObjectOptions: props.mapObjectOptions,
  clusterOptions: props.clusterOptions,
  balloonOptions: props.balloonOptions,
  markerOptions: props.markerOptions,
}));
const displayMapLoading = computed(
  () =>
    props.mapLoading === true || innerState.value === ShopSelectState.LOADING,
);
const loadingError = computed(() => innerState.value === ShopSelectState.ERROR);

const customOptions = computed<IMapCustomOptions>(() => {
  const zoomRange = [props.minZoom, props.maxZoom].sort((a, b) => a - b);
  return {
    zoomRange,
    centerZoom: props.centerZoom ?? zoomRange[1],
    controls: props.controls,
  };
});
const displayConfig = computed(() =>
  getDisplayConfiguration(props.shops ?? [], props.selectedShop ?? null),
);
const searchnEabled = computed(() => {
  return (
    !props.shopListInBoundingBox ||
    !mapBoundingBoxRef.value ||
    props.shopsSearchString?.length >= props.minShopsSearchStringLength
  );
});
const displayListShops = computed(() => {
  if (searchnEabled.value) return displayMapShops.value;
  return filterShopsInBoundingBox(
    mapBoundingBoxRef.value!,
    displayMapShops.value,
  ) as IDisplayShop[];
});
const displayMapShops = computed(() => {
  const list = displayConfig.value?.list ?? [];
  const displayStoreTypes = shopsFilter.value;
  if (!shopsFilter.value?.length || !list.length) return list;
  return list.filter((s) => displayStoreTypes.includes(s.storeType));
});
const filtersList = computed(() => {
  if (props.shopsFilterOptions !== null) return props.shopsFilterOptions;
  const src = searchnEabled.value
    ? displayListShops.value
    : displayMapShops.value;
  const presentedTypes: ShopSelectShopType[] = uniqBy(src, "storeType").map(
    (s) => s.storeType,
  );
  return FILTERS_SHOPS_ARRAY.filter((s) => {
    return s.type === null || presentedTypes.includes(s.type);
  });
});
const modalShopListState = computed(() => {
  if (props.shopsListLoading) return ShopSelectState.LOADING;
  if (loadingError.value) return ShopSelectState.ERROR;
  return ShopSelectState.READY;
});
const className = computed(() => ({
  "shop-select--modal-open": !!modalOpen.value,
  "shop-select--modal-maximized":
    modalExpanded.value === ShopModalState.MAXIMIZED,
  "shop-select--modal-minimized":
    modalExpanded.value === ShopModalState.MINIMIZED,
}));
const setMapError = (err?: unknown) => {
  console.error({ err });
  innerState.value = ShopSelectState.ERROR;
};
const setMapReady = () => (innerState.value = ShopSelectState.READY);

onMounted(() => {
  mapElementId.value = `map-el--${new Date().getTime()}`;
  const win = window as any;
  const onLoad = () => {
    win.ymaps.ready(() => {
      emit("ready:script");
      setMapReady();
    });
  };
  const onErr = (err: unknown) => {
    // eslint-disable-next-line no-console
    console.warn("VShopSelect -- error while trying to fetch YM script: ", {
      err,
    });
    emit("error:script", { err });
    setMapError();
  };
  if (!props.yandexMapKey && !win.ymaps)
    return onErr({ message: "empty yandexMapKey" });
  else if (win.ymaps) return onLoad();
  else {
    const scriptSrc = `https://api-maps.yandex.ru/${props.yandexMapVersion}/?apikey=${props.yandexMapKey}&lang=${props.yandexMapLang}`;
    fetchExternalScript(scriptSrc, onLoad, onErr);
  }
});
const handleShopSelect = (code: string) => {
  const shop = props.shops?.find((s) => s.code === code);
  if (!shop) return;
  emit("submit:shopSelect", { shop });
};

const handleClickClose = () => emit("close:shopSelect");
const handleModalOpen = () => {
  modalOpen.value = true;
};
const handleModalClose = () => {
  send("ShopSelect:Search:Close");
  send("ShopSelect:MapStore:Close:Click");
  modalOpen.value = false;
};
const handleUserMapManipulation = () => {
  modalExpanded.value = ShopModalState.MINIMIZED;
};
const handleSubmitSearch = (val = "") => {
  shopsSearchValueRef.value = val;
  forceSearchSubmit();
};
const setTargetShopRef = (code: string, eventName: string) => {
  const shop = props.shops?.find((s) => s.code === code);
  if (!shop) return;
  send(eventName, { ...shop });
  targetShopRef.value = shop;
};
const clearTargetShopRef = () => {
  targetShopRef.value = null;
};

watch(
  () => props.targetShop,
  (next) => {
    if (!next || targetShopRef.value?.code === next?.code) return;
    targetShopRef.value = next;
  },
);
watch(targetShopRef, (next) => {
  if (!next?.code || modalExpanded.value !== ShopModalState.MAXIMIZED) return;
  modalExpanded.value = ShopModalState.NORMAL;
});
watch(
  () => props.shopsSearchString,
  (next) => {
    if (next === shopsSearchValueRef.value) return;
    clearSearchDebounce();
    shopsSearchValueRef.value = next;
  },
);
watch(
  () => props.boundingBox,
  (next) => {
    clearBoundsChangeDebounce();
    mapBoundingBoxRef.value = next;
  },
);
watch(shouldMapBeReinitialized, () => {
  mapOptionsKeyRef.value += 1;
});
watch(modalOpen, (v) => v === true && send("ShopSelect:List:View"), {
  immediate: true,
});
watch(
  () => geolocationPermission.state.value,
  (v) => {
    switch (v) {
      case "prompt":
        send("ShopSelect:GeoPerm:View");
        break;
      case "granted":
        send("ShopSelect:GeoPerm:Click:Yes");
        break;
      case "denied":
        send("ShopSelect:GeoPerm:Click:No");
        break;
    }
  },
);

onUnmounted(() => {
  clearBoundsChangeDebounce();
  clearSearchDebounce();
});
</script>

<template>
  <section class="shop-select__outer">
    <div class="shop-select" :class="className">
      <ShopSelectCloseButton
        v-if="displayCloseButton"
        @click:close="handleClickClose"
      />
      <ShopSelectShops
        v-if="displayModal"
        v-model:filter-value="shopsFilter"
        v-model:modalState="modalExpanded"
        v-model:search-value="shopsSearchValue"
        :open="modalOpen"
        :state="modalShopListState"
        :shops="displayListShops"
        :total="displayListShops.length"
        :display-fields="displayConfig.shopListDisplayFields"
        :filters="filtersList"
        :list-empty-component="listEmptyComponent"
        @click:list-item="
          (c: string) => setTargetShopRef(c, `ShopSelect:ListStore:Store:Click`)
        "
        @click:list-item-select="handleShopSelect"
        @click:modal-close="handleModalClose"
        @submit:search-value="handleSubmitSearch"
      />
      <ShopSelectModalButton
        v-if="displayModal && !modalOpen"
        @click:modal-button="handleModalOpen"
      />
      <ShopSelectMap
        :key="mapOptionsKeyRef"
        v-model:boundingBox="mapBoundingBox"
        :selected-shop="selectedShop"
        :target-shop="targetShopRef"
        :display-map="displayMap"
        :loading="displayMapLoading"
        :loading-error="loadingError"
        :map-element-id="mapElementId"
        :center="center"
        :zoom="zoom"
        :controls="controls"
        :custom-options="customOptions"
        :shops="displayMapShops"
        :map-object-options="mapObjectOptions"
        :geolocation-object-options="geolocationObjectOptions"
        :marker-options="markerOptions"
        :cluster-options="clusterOptions"
        :balloon-options="balloonOptions"
        :clusterize="clusterize"
        :autocenter-key="autocenterKey"
        @open:balloon="
          (c: string) => setTargetShopRef(c, `ShopSelect:MapStore:Store:Click`)
        "
        @drag:map="handleUserMapManipulation"
        @close:balloon="clearTargetShopRef"
        @click:balloon-select="handleShopSelect"
        @error:map="setMapError"
      />
    </div>
  </section>
</template>
<style src="./assets/css/map.css" />
<style lang="postcss" scoped>
.shop-select {
  position: relative;
  margin: 0;
  width: 100%;
  height: 100%;

  &__outer {
    position: fixed;
    inset: 0;
    background: #f0f0f0;
    z-index: 1001;
  }
}
</style>
